import React, { useEffect, useState } from 'react';
import { cacheOwner } from './profileCache';
import { localStore } from './localStore';
import API from '../config/api';

export async function downloadMedia(url: string) {
  const owner = cacheOwner();
  if (!owner || !url || /^(data:|blob:)/.test(url)) return;
  const target = new URL(url, API.BASE_URL);
  const allowed = [new URL(API.BASE_URL).origin, 'https://firebasestorage.googleapis.com', 'https://storage.googleapis.com'];
  if (!allowed.includes(target.origin)) throw new Error('Attachment host is unavailable for offline downloading.');
  const response = await fetch(target.href, { credentials: 'omit', signal: AbortSignal.timeout(20000) });
  if (!response.ok) throw new Error('Attachment download failed.');
  if (Number(response.headers.get('content-length')) > 25 * 1024 * 1024) throw new Error('Attachment exceeds the offline download limit.');
  const blob = await response.blob();
  if (blob.size > 25 * 1024 * 1024) throw new Error('Attachment exceeds the offline download limit.');
  if (cacheOwner() !== owner) throw new Error('Account changed.');
  await localStore.put('files', { key: JSON.stringify([owner, target.href]), owner, blob, savedAt: Date.now() });
}
export async function savedMediaUrl(url: string) {
  const owner = cacheOwner();
  if (!owner || navigator.onLine || /^(data:|blob:)/.test(url)) return url;
  const saved: any = await localStore.get('files', JSON.stringify([owner, new URL(url, API.BASE_URL).href]));
  if (cacheOwner() !== owner || !saved) throw new Error('This attachment was not downloaded.');
  return URL.createObjectURL(saved.blob);
}
export function OfflineImage(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [src, setSrc] = useState(props.src);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setMissing(false);
    if (props.src) void savedMediaUrl(props.src).then(value => {
      if (value !== props.src && value.startsWith('blob:')) objectUrl = value;
      if (active) setSrc(value); else if (objectUrl) URL.revokeObjectURL(objectUrl);
    }).catch(() => { if (active) setMissing(true); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [props.src]);
  return missing ? <span>Attachment not downloaded on this device</span> : <img {...props} src={src} alt={props.alt || 'Patient attachment'} />;
}
export function OfflineAudio(props: React.AudioHTMLAttributes<HTMLAudioElement>) {
  const [src, setSrc] = useState(props.src);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setMissing(false);
    if (props.src) void savedMediaUrl(props.src).then(value => {
      if (value !== props.src && value.startsWith('blob:')) objectUrl = value;
      if (active) setSrc(value); else if (objectUrl) URL.revokeObjectURL(objectUrl);
    }).catch(() => { if (active) setMissing(true); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [props.src]);
  return missing ? <span>Audio not downloaded on this device</span> : <audio {...props} src={src} />;
}
